/**
 * Health Score API Service - EVOP Inspector
 * 
 * Dedicated service for health score related API calls
 */

import { apiClient } from './api'
import { AxiosResponse } from 'axios'

// Health Score Data Types
export interface DomainScore {
  band: 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured' | 'No Data'
  color: 'green' | 'orange' | 'red' | 'black' | 'grey'
  score: number | null
  exception_count: number
}

export interface FranchiseHealthScore {
  fp_code: string
  franchise_name: string
  country: string
  region: string
  state: string
  fp_status: string
  last_sync_date?: string
  overall_health: {
    band: 'Good' | 'Watch' | 'Poor' | 'Critical' | 'Not Configured' | 'No Data'
    color: 'green' | 'orange' | 'red' | 'black' | 'grey'
    score: number | null
  }
  product_score: DomainScore | null
  sale_score: DomainScore | null
  system_score: DomainScore | null
  system_connectivity?: {
    lightspeed: boolean
    xero: boolean
    field_management: boolean
  }
}

export interface HealthScoresSummary {
  total_fps: number
  by_band: {
    Good: number
    Watch: number
    Poor: number
    Critical: number
    'No Data': number
  }
}

export interface HealthScoresResponse {
  summary: HealthScoresSummary
  franchises: FranchiseHealthScore[]
}

export interface ExceptionDetail {
  report_type: string
  name_short: string
  exception_count: number
  health_score?: number
  health_band: string
  severity: 'info' | 'warning' | 'critical'
  inspector_url: string
}

export interface HealthScoreDomainDetails {
  domain: string
  band: string
  score: number | null
  total_exceptions: number
  exception_details: ExceptionDetail[]
  last_calculated: string
  score_methodology: string
}

export interface HealthScoreHistoryEntry {
  date: string
  score: number | null
  band: string
  color: string
  exception_count: number
  sync_operation_id?: string
  exception_breakdown?: any
}

export interface HealthScoreHistory {
  fp_code: string
  days: number
  domain_filter: string | null
  history: { [domain: string]: HealthScoreHistoryEntry[] }
  retrieved_at: string
}

export interface TrendData {
  direction: 'improving' | 'declining' | 'stable'
  change: number
  latest_score: number
  earliest_score: number
  latest_date: string
  earliest_date: string
  period_days: number
}

export interface HealthScoreTrends {
  fp_code: string
  period: string
  trends: { [domain: string]: TrendData }
  retrieved_at: string
}

export interface HealthScoreChange {
  id: number
  domain: string
  score: number | null
  band: string
  color: string
  exception_count: number
  calculated_at: string
  sync_operation_id?: string
  exception_breakdown?: any
}

export interface HealthScoreChanges {
  fp_code: string
  date: string
  changes: HealthScoreChange[]
  total_changes: number
  retrieved_at: string
}

export interface ComparisonEntry {
  fp_code: string
  franchise_name: string
  market: string
  region_code: string
  score: number | null
  band: string
  color: string
  exception_count: number
  calculated_at: string | null
}

export interface ComparisonStats {
  count: number
  average: number
  min: number
  max: number
  range: number
}

export interface HealthScoreComparison {
  metric: string
  requested_fp_codes: string[]
  found_count: number
  missing_fp_codes: string[]
  comparisons: ComparisonEntry[]
  statistics: ComparisonStats
  retrieved_at: string
}

export interface Region {
  region_code: string
  region_name: string
  market: string
  description?: string
}

export interface RegionsResponse {
  regions: Region[]
}

/**
 * Health Score API Service Class
 */
class HealthScoreApiService {
  /**
   * Get health scores for all franchise partners
   * @param market Filter by market (ANZ, USA)
   * @param region Filter by region code
   */
  async getHealthScores(
    market?: string,
    region?: string
  ): Promise<HealthScoresResponse> {
    const params = new URLSearchParams()
    if (market) params.append('market', market)
    if (region) params.append('region', region)
    
    const response: AxiosResponse<HealthScoresResponse> = await apiClient.get(
      `/admin/health-scores${params.toString() ? `?${params.toString()}` : ''}`
    )
    return response.data
  }

  /**
   * Get available regions for filtering
   * @param market Filter by market (ANZ, USA)
   */
  async getRegions(market?: string): Promise<RegionsResponse> {
    const params = new URLSearchParams()
    if (market) params.append('market', market)

    const response: AxiosResponse<RegionsResponse> = await apiClient.get(
      `/admin/health-scores/filter-regions${params.toString() ? `?${params.toString()}` : ''}`
    )
    return response.data
  }

  /**
   * Get detailed health score breakdown for a specific domain
   * @param fpCode Franchise partner code
   * @param domain Domain (product, sale, system)
   */
  async getDomainDetails(
    fpCode: string,
    domain: string
  ): Promise<HealthScoreDomainDetails> {
    const response: AxiosResponse<HealthScoreDomainDetails> = await apiClient.get(
      `/admin/health-scores/${fpCode}/domain/${domain}`
    )
    return response.data
  }

  /**
   * Get health score history for a franchise partner
   * @param fpCode Franchise partner code
   * @param days Number of days of history (default 30)
   * @param domain Filter by domain (optional)
   */
  async getHistory(
    fpCode: string,
    days: number = 30,
    domain?: string
  ): Promise<HealthScoreHistory> {
    const params = new URLSearchParams()
    params.append('days', days.toString())
    if (domain) params.append('domain', domain)
    
    const response: AxiosResponse<HealthScoreHistory> = await apiClient.get(
      `/admin/health-scores/${fpCode}/history?${params.toString()}`
    )
    return response.data
  }

  /**
   * Get health score trends for a franchise partner
   * @param fpCode Franchise partner code
   * @param period Trend period ('week', 'month', 'quarter')
   */
  async getTrends(
    fpCode: string,
    period: 'week' | 'month' | 'quarter' = 'week'
  ): Promise<HealthScoreTrends> {
    const params = new URLSearchParams()
    params.append('period', period)
    
    const response: AxiosResponse<HealthScoreTrends> = await apiClient.get(
      `/admin/health-scores/${fpCode}/trends?${params.toString()}`
    )
    return response.data
  }

  /**
   * Get health score changes for a specific date
   * @param fpCode Franchise partner code
   * @param date Date in YYYY-MM-DD format
   */
  async getChanges(
    fpCode: string,
    date: string
  ): Promise<HealthScoreChanges> {
    const params = new URLSearchParams()
    params.append('date', date)
    
    const response: AxiosResponse<HealthScoreChanges> = await apiClient.get(
      `/admin/health-scores/${fpCode}/changes?${params.toString()}`
    )
    return response.data
  }

  /**
   * Compare health scores across multiple franchise partners
   * @param fpCodes Array of franchise partner codes
   * @param metric Metric to compare ('overall', 'product', 'sale', 'system')
   */
  async compareScores(
    fpCodes: string[],
    metric: 'overall' | 'product' | 'sale' | 'system' = 'overall'
  ): Promise<HealthScoreComparison> {
    const params = new URLSearchParams()
    params.append('fp_codes', fpCodes.join(','))
    params.append('metric', metric)
    
    const response: AxiosResponse<HealthScoreComparison> = await apiClient.get(
      `/admin/health-scores/compare?${params.toString()}`
    )
    return response.data
  }
}

// Export singleton instance
export const healthScoreApi = new HealthScoreApiService()

// Export individual functions for convenience
export const {
  getHealthScores,
  getRegions,
  getDomainDetails,
  getHistory,
  getTrends,
  getChanges,
  compareScores,
} = healthScoreApi